import React, {
  type AnchorHTMLAttributes,
  forwardRef,
  useCallback,
  useMemo,
  useSyncExternalStore,
} from "react";

/**
 * Workspace-aware routing over the History API.
 *
 * Private surfaces live under `/w/:workspaceId`, and every path written in a
 * component is workspace-relative (`/people`, not `/w/abc/people`) so nothing
 * has to remember to carry the workspace. `usePathname` strips the prefix back
 * off, which is what the shell matches against.
 *
 * `popstate` gives correct Back and Forward behaviour directly; the demanding
 * part here is query state — which drawer is open, which filters are applied —
 * and that must survive both.
 */

type Listener = () => void;
const listeners = new Set<Listener>();

/**
 * Set once by the app root. Keeps `router.refresh()` working without every
 * caller having to reach for the query client.
 */
let invalidateAll: (() => void) | undefined;

export function setDataInvalidator(invalidate: () => void): void {
  invalidateAll = invalidate;
}

export function refreshData(): void {
  invalidateAll?.();
}

function notify() {
  for (const listener of listeners) listener();
}

function subscribe(listener: Listener) {
  listeners.add(listener);
  window.addEventListener("popstate", listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("popstate", listener);
  };
}

const snapshot = () => `${window.location.pathname}${window.location.search}`;

function useHref(): string {
  return useSyncExternalStore(subscribe, snapshot, () => "/");
}

export function useWorkspaceId(): string {
  const href = useHref();
  return /^\/w\/([^/?]+)/.exec(href)?.[1] ?? "";
}

export function workspaceHref(workspaceId: string, path: string): string {
  return workspaceId && path.startsWith("/") && !path.startsWith("//") && !path.startsWith("/w/")
    ? `/w/${workspaceId}${path === "/" ? "" : path}`
    : path;
}

/** The path with the workspace prefix removed, so routes read `/people`. */
export function usePathname(): string {
  const href = useHref();
  const [pathname = "/"] = href.split("?");
  return pathname.replace(/^\/w\/[^/]+/, "") || "/";
}

export function useSearchParams(): URLSearchParams {
  const href = useHref();
  const [, query = ""] = href.split("?");
  return useMemo(() => new URLSearchParams(query), [query]);
}

export function navigate(to: string, options: { replace?: boolean } = {}): void {
  if (to === window.location.pathname + window.location.search + window.location.hash) {
    return;
  }
  window.history[options.replace ? "replaceState" : "pushState"](null, "", to);
  notify();
}

/**
 * Stable across renders: callers hold on to this, and a fresh object every render
 * would invalidate their callbacks and remount everything built from one.
 */
export function useRouter() {
  const workspaceId = useWorkspaceId();
  return useMemo(
    () => ({
      push: (path: string, _options?: unknown) => navigate(workspaceHref(workspaceId, path)),
      replace: (path: string, _options?: unknown) =>
        navigate(workspaceHref(workspaceId, path), { replace: true }),
      back: () => window.history.back(),
      forward: () => window.history.forward(),
      /**
       * The server-render refresh has no equivalent here; data lives in the
       * query cache, so refreshing means invalidating it.
       */
      refresh: () => refreshData(),
    }),
    [workspaceId],
  );
}

/** Applies query changes while preserving every other parameter. */
export function queryHref(
  pathname: string,
  params: URLSearchParams,
  changes: Record<string, string | null>,
): string {
  const next = new URLSearchParams(params);
  for (const [key, value] of Object.entries(changes)) {
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
  }
  const rendered = next.toString();
  return `${pathname}${rendered ? `?${rendered}` : ""}`;
}

/**
 * View changes that should not discard an editor draft or refetch a page.
 * Reads from `window.location` so concurrent updates compose.
 */
export function useUrlState() {
  const params = useSearchParams();
  const update = useCallback((changes: Record<string, string | null>, replace = false) => {
    const href =
      queryHref(window.location.pathname, new URLSearchParams(window.location.search), changes) +
      window.location.hash;
    navigate(href, { replace });
  }, []);
  return { params, update };
}

/** A single query parameter, preserving the rest. */
export function useSearchParam(
  key: string,
): [string | null, (value: string | null, options?: { replace?: boolean }) => void] {
  const { params, update } = useUrlState();
  const set = useCallback(
    (value: string | null, options: { replace?: boolean } = {}) =>
      update({ [key]: value }, options.replace ?? false),
    [key, update],
  );
  return [params.get(key), set];
}

/**
 * Link behaviour for an element that is already an anchor.
 *
 * A plain function rather than a hook, because the shell applies it inside a
 * list and hooks may not be called in a loop.
 *
 * The design system's nav items are styled anchors. Wrapping them in another
 * link would nest one anchor inside another, which is invalid and which React
 * reports at runtime, so they take these props instead.
 */
export function linkProps(
  workspaceId: string,
  href: string,
  /**
   * Extra click behaviour, composed rather than declared alongside.
   *
   * Spreading these props and then writing your own `onClick` replaces this
   * one, which leaves a real anchor that reloads the document — a full page
   * flash on every click. Passing the handler here makes that impossible.
   */
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void,
): { href: string; onClick: (event: React.MouseEvent<HTMLAnchorElement>) => void } {
  const resolved = workspaceHref(workspaceId, href);
  return {
    href: resolved,
    onClick: (event) => {
      onClick?.(event);
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }
      event.preventDefault();
      navigate(resolved);
    },
  };
}

type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & { href: string };

/**
 * A workspace-aware anchor. It is a real anchor, so middle-click, copy link and
 * open-in-new-tab all behave as people expect; only the plain left click is
 * intercepted for client navigation.
 */
export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { href, onClick, ...rest },
  ref,
) {
  const workspaceId = useWorkspaceId();
  const resolved = workspaceHref(workspaceId, href);

  return (
    <a
      {...rest}
      ref={ref}
      href={resolved}
      onClick={(event) => {
        onClick?.(event);
        if (
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey ||
          rest.target === "_blank"
        ) {
          return;
        }
        event.preventDefault();
        navigate(resolved);
      }}
    />
  );
});
